import {cloudPathFromRel, tryParseCloudPath} from '@/shared/utils/cloudPath'

/** Cognito identity id segment (`<region>:<uuid>`) that roots every user's S3 space. */
const IDENTITY_SEGMENT = /^[a-z]{2}(?:-[a-z]+)+-\d:[0-9a-f-]{36}$/i

function stripIdentity(key: string, identityId?: string): string {
  if (identityId && (key === identityId || key.startsWith(`${identityId}/`))) {
    return key.slice(identityId.length)
  }
  const [first, ...rest] = key.split('/')
  return IDENTITY_SEGMENT.test(first) ? rest.join('/') : key
}

/**
 * Canonical `cloud:` path for a storage reference, e.g.
 * `s3://bucket/eu-west-1:1234…/chat-test/abc/` → `cloud:chat-test/abc/`.
 *
 * Accepts a `cloud:` path, an `s3://` URI, an identity-prefixed key or a root-relative key (a
 * file-table row id). `isFolder` forces the folder's trailing `/`. Returns `null` when the result is
 * not a valid cloud path (e.g. a `..` segment).
 */
export function toCloudPath(
  keyOrPath: string,
  options: {isFolder?: boolean; identityId?: string} = {}
): string | null {
  const trimmed = keyOrPath.trim()
  if (trimmed.startsWith('cloud:')) return tryParseCloudPath(trimmed)?.cloud ?? null
  const withoutScheme = trimmed.replace(/^s3:\/\/[^/]+\//, '')
  const rel = stripIdentity(withoutScheme.replace(/^\/+/, ''), options.identityId)
  try {
    return cloudPathFromRel(rel, {isFolder: options.isFolder})
  } catch {
    return null
  }
}
